// SHARED — keep in sync across pro/care/backbar/academy (canonical: workspace/shared-web-auth/). Extract to @rootegra/auth later.
//
// The persisted "which hat am I wearing" choice + a fetch wrapper that stamps
// it onto every request as x-active-role / x-active-salon. The headers FILTER
// among rows the user actually owns — they never grant anything (forged or
// unheld combos fail closed server-side), so sending them on every request,
// including auth calls, is safe.

export type ActiveRole = 'staff' | 'client' | 'admin';

export type ActiveContext = {
  role: ActiveRole;
  /** Scoped salon uuid; null for admin or salonless contexts. */
  salonId: string | null;
};

export const ACTIVE_CONTEXT_STORAGE_KEY = 'rootegra.activeContext';

const ROLES: readonly ActiveRole[] = ['staff', 'client', 'admin'];

/** Read the persisted context. Returns null when absent or malformed. Never throws. */
export function loadActiveContext(): ActiveContext | null {
  try {
    if (typeof window === 'undefined' || !('localStorage' in window)) return null;
    const raw = window.localStorage.getItem(ACTIVE_CONTEXT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { role?: unknown; salonId?: unknown };
    if (!ROLES.includes(parsed?.role as ActiveRole)) return null;
    const salonId = typeof parsed.salonId === 'string' && parsed.salonId ? parsed.salonId : null;
    return { role: parsed.role as ActiveRole, salonId };
  } catch {
    return null;
  }
}

/** Persist the context. Never throws. */
export function saveActiveContext(ctx: ActiveContext): void {
  try {
    if (typeof window === 'undefined' || !('localStorage' in window)) return;
    window.localStorage.setItem(ACTIVE_CONTEXT_STORAGE_KEY, JSON.stringify(ctx));
  } catch {
    /* ignore — worst case the server falls back to legacy collapse order */
  }
}

/** Forget the context (e.g. on sign-out). Never throws. */
export function clearActiveContext(): void {
  try {
    if (typeof window === 'undefined' || !('localStorage' in window)) return;
    window.localStorage.removeItem(ACTIVE_CONTEXT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Build a fetch wrapper that injects the active-context headers. Wire it in as
 * the Supabase client's options.global.fetch:
 *
 *   createRootegraClient({ global: { fetch: makeContextFetch(loadActiveContext) } })
 *
 * getCtx is read per-request, so in-place context switches (multi-salon staff)
 * take effect immediately without rebuilding the client. When getCtx returns
 * null, no headers are added and the server applies its legacy collapse.
 */
export function makeContextFetch(getCtx: () => ActiveContext | null): typeof fetch {
  return (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const ctx = getCtx();
    if (!ctx) return fetch(input, init);

    // Preserve headers from either the init object or a Request input.
    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    headers.set('x-active-role', ctx.role);
    if (ctx.salonId) {
      headers.set('x-active-salon', ctx.salonId);
    } else {
      headers.delete('x-active-salon');
    }

    // Note: when input is a Request, passing `headers` in init replaces the
    // Request's own headers (per the fetch spec) — which is exactly what we
    // want, since we copied them above before stamping ours on.
    return fetch(input, { ...init, headers });
  };
}
